import { DateTime } from 'luxon';
import { Mutex } from '@cf-shared/mutex';
import { getEntryLocalTimeInDesiredTimeZone, Log_Entry, getEntryTimeFromID as getEntryTimeFromId, LogRecordType } from '@cf-shared/data-model/user-log';
import { Log_Workout } from '@cf-shared/data-model/log-workout';
// Type-only on purpose: this module is loaded under plain node by its unit tests, and a
// value import of either class would drag PouchDB and Angular in with it.
import type { DatabaseWrapper } from 'services/database-wrapper';
import type { LoggingService } from './logging.service';
import { describeIncomingRevRelation, describeRevGap, shouldReportBacklogRevStamp, staleCopyWouldChangeContent } from './sync-revision-decisions';

/**
 * The most documents one session may report from `reportRevStampLaundering` below.
 * Whatever orphans a cache entry can keep doing it, and a report nobody has read yet is
 * not worth an unbounded number of Sentry events.
 */
const MAX_REPORTED_REV_STAMP_DOCUMENTS = 20;

/**
 * Documents already reported this session, so a lingering orphan is counted once rather
 * than once per backlog regeneration.
 *
 * Module-level rather than instance state because `mergeLogEntries` is a free function
 * called from two classes. Same pattern, and same test-only reset, as the session-health
 * bookkeeping in `session-health.ts`.
 *
 * Accepted limitation: the ids are not keyed by database, so an impersonation switch
 * could let one account's document id suppress the report for another account's document
 * with the same id. Log entry ids carry no user component, so that is possible in
 * principle. It is harmless in practice because the only live impersonation switch
 * (`routeInitialImpersonationDb`) runs at init, before this cache holds anything.
 */
const reportedRevStampDocIds = new Set<string>();

/** Reset the per-session report bookkeeping. For tests only. */
export function resetBacklogRevStampReportsForTests(): void {
  reportedRevStampDocIds.clear();
}

/**
 * Say that the backlog cache is about to hand its own copy of an entry the revision of
 * the row just read from disk (#4835).
 *
 * # Why this is reported at all
 *
 * The line below this call keeps the CACHED body and takes the DISK revision. Together
 * those two make a body that no revision-comparing check can question: by the time the
 * identity map looks, the copy claims the revision the database holds, so
 * `decideCanonicalUpdateAction` answers `Overwrite` however far behind the body really
 * is, and the older content is copied over the shared object every page reads with
 * nothing written down. That is how the customer in #4832 lost a set he had already
 * logged. This is the only place where both bodies and both true revisions are still
 * readable, so it is the only place the fact can be recorded.
 *
 * # It reports, and changes nothing
 *
 * The stamp still happens exactly as it did. A structural repair here is not the same
 * argument as the one that closed the database-body door in #4860: a body the database
 * produced carries no unsaved work by construction, but the object the cache is holding
 * is caller-held and can carry edits made in place that were never saved. Preferring the
 * disk body would trade one unmeasured loss for another, in a cache every Home list
 * depends on. What that decision needs first is this number.
 *
 * Read `design-docs/sync-integrity.md`, "An out-of-date copy handed to the identity map",
 * before changing any of this — it records two repairs for the wider problem that were
 * built, measured worse, and must not be rebuilt.
 *
 * @param cachedEntry the object the backlog cache is holding
 * @param diskEntry the row the disk read produced, which is already the shared object
 */
function reportRevStampLaundering(
  logging: LoggingService | undefined,
  cachedEntry: Log_Entry,
  diskEntry: Log_Entry,
): void {
  if (!logging) return;
  // the whole condition: two different objects for one id. When they are the same object
  // the stamp assigns a revision to itself and nothing happens - see
  // shouldReportBacklogRevStamp for why that is the only question worth asking here.
  if (!shouldReportBacklogRevStamp({
    isSameObject: cachedEntry === diskEntry,
    alreadyReportedThisSession: reportedRevStampDocIds.has(cachedEntry._id),
    reportedDocumentCount: reportedRevStampDocIds.size,
    maxReportedDocuments: MAX_REPORTED_REV_STAMP_DOCUMENTS,
  })) {
    return;
  }
  reportedRevStampDocIds.add(cachedEntry._id);

  // walks both documents, so it runs only on this branch, which is already the rare one
  const contentDiffers = staleCopyWouldChangeContent(diskEntry, cachedEntry);
  logging.trackSilentProblem(
    'backlog-cache: a cached copy took the disk revision, hiding a stale overwrite',
    {
      docId: cachedEntry._id,
      // both revision strings, read BEFORE the stamp - afterwards they are equal and say
      // nothing. High-cardinality, so context rather than tags.
      cachedRev: cachedEntry._rev ?? null,
      diskRev: diskEntry._rev ?? null,
    },
    {
      // where the cache's copy stands against the database: `older` is the loss this was
      // built to find, `newer` means the cache is ahead and the stamp moves it BACKWARDS,
      // and `same` means the two disagree on content at one revision.
      backlog_stamp_rev_relation: describeIncomingRevRelation(diskEntry._rev, cachedEntry._rev),
      // how far behind, bucketed so the tag stays low-cardinality
      backlog_stamp_rev_gap: describeRevGap(diskEntry._rev, cachedEntry._rev),
      // whether this particular one destroys anything. Deliberately a tag and not a
      // condition: "the cache holds objects it should not" and "those objects destroy
      // content" are different questions and a reader needs both answered.
      backlog_stamp_content_differs: contentDiffers ? 'true' : 'false',
      // a copy with no revision at all is a generated entry that was never saved, about
      // to be given a real document's revision. Its own shape, worth counting apart.
      backlog_stamp_cached_has_rev: cachedEntry._rev ? 'true' : 'false',
      backlog_stamp_entry_type: cachedEntry.type,
    }
  );
}

/**
 * Merge two sets of log entries by _id. entries1 takes priority for data.
 *
 * @param revSource Controls which source is authoritative for _rev:
 *   - 'entries2': Always use entries2's _rev (use when entries2 is from disk - disk is source of truth)
 *   - 'entries1': Keep entries1's _rev, only copy from entries2 if entries1 has no _rev (use when entries1 already has correct _rev from a previous disk merge)
 * @param logging optional, and only the 'entries2' direction uses it: that direction keeps
 *   one body while taking the other's revision, which no later check can question, so it
 *   reports the case here. See `reportRevStampLaundering` below (#4835). Without it the
 *   merge behaves identically and simply records nothing.
 */
export function mergeLogEntries(
  entries1: Log_Entry[],
  entries2: Log_Entry[],
  database?: DatabaseWrapper,
  revSource: 'entries1' | 'entries2' = 'entries2',
  logging?: LoggingService,
): Log_Entry[] {
  const entriesById = new Map<string, Log_Entry>();

  // first add all entries from entries2
  for (const entry of entries2) {
    if (entry._id) {
      entriesById.set(entry._id, entry);
    }
  }

  // then add/overwrite with entries from entries1 (entries1 takes priority for data)
  for (const entry of entries1) {
    if (entry._id) {
      const existingEntry = entriesById.get(entry._id);

      if (existingEntry && existingEntry._rev) {
        if (revSource === 'entries2') {
          // #4835: the assignment below keeps this entry's body and gives it the disk
          // row's revision, which is invisible to every revision-comparing check
          // afterwards. Say so first, while both revisions are still readable.
          reportRevStampLaundering(logging, entry, existingEntry);
          // entries2 (disk) is authoritative - always use its _rev
          entry._rev = existingEntry._rev;
        } else {
          // entries1 is authoritative - only copy if entries1 has no _rev
          if (!entry._rev) {
            entry._rev = existingEntry._rev;
          }
        }
      }
      entriesById.set(entry._id, entry);
    }
  }

  // convert map to array and sort by time
  let entries = Array.from(entriesById.values());
  entries.sort((a, b) => a.time_utc.localeCompare(b.time_utc));

  // ensure all returned entries use canonical documentCache references
  if (database) {
    return entries.map(entry => database.ensureCachedReference(entry));
  }
  return entries;
}

/**
 * Dependencies for BacklogCache, injected to avoid circular imports with UserDb.
 * Note: database is a getter because it's initialized after UserDb construction.
 */
export interface BacklogCacheDeps {
  generateBacklogInMemoryAsync: (start: DateTime, end: DateTime, snapshot: Log_Entry[]) => Promise<Log_Entry[]>;
  getDatabase: () => DatabaseWrapper;
  logging: LoggingService;
}

/**
 * BacklogCache: Caches generated schedule entries to avoid expensive regeneration.
 *
 * CRITICAL INVARIANTS (from postmortem analysis):
 * 1. Cache entries MUST use canonical documentCache references (via ensureCachedReference)
 * 2. Cache MUST be cleared when sync pulls new log entries (reactive sync updates canonical
 *    refs in-place via DocWatcher; on-demand date-range sync clears affected days via syncLogDateRangeAsync)
 * 3. Concurrent cache updates MUST be serialized via cachingLock
 * 4. Async operations MUST check generation counter to detect invalidation
 *
 * CACHE FLOW:
 *   Request for entries in range [Jan 15 - Jan 20]
 *   ├── Are all days cached? → Yes → Filter and return from cache (hit)
 *   └── No → updateCacheAsync() (miss)
 *       1. Capture generation counter and snapshot
 *       2. Load entries from disk
 *       3. Generate schedule entries
 *       4. Canonicalize all entries (ensureCachedReference)
 *       5. Check generation (abort if changed - stale discard)
 *       6. Merge into cache
 *
 * THREAD SAFETY:
 * - Public async methods (getEntryByIdAsync, getEntriesInRangeAsync, clear) use cachingLock
 * - findPreviousWorkoutInCache is synchronous read-only, acceptable for UI reads
 * - getCachedEntriesSnapshot returns a shallow copy safe for async operations
 *
 * @see design-docs/postmortem-pouchdb-rev-conflicts.md
 */
export class BacklogCache {
  private _entries: Log_Entry[] = [];
  private _cachedDays: Set<string> = new Set();

  private cachingLock = new Mutex();

  // Generation counter - increments on each cache clear to detect stale operations
  private generation = 0;

  // Counter for active operations to coordinate with cache clear
  private activeOperations = 0;

  // Stats for observability
  private stats = {
    hits: 0,
    misses: 0,
    clears: 0,
    staleDiscards: 0,
  };

  constructor(private deps: BacklogCacheDeps) {}

  // --- Observability Accessors ---

  getCacheSize(): number {
    return this._entries.length;
  }

  getCachedDays(): readonly string[] {
    return [...this._cachedDays];
  }

  /**
   * The earliest day this cache currently holds, as YYYY-MM-DD, or undefined when it holds
   * nothing. Observability only — it says nothing about whether the days in between are held,
   * so never judge a look-back with it; `earliestUnbrokenCachedDay` is what answers that.
   */
  getEarliestCachedDay(): string | undefined {
    let earliest: string | undefined = undefined;
    for (const day of this._cachedDays) {
      if (earliest === undefined || day < earliest) {
        earliest = day;
      }
    }
    return earliest;
  }

  getGenerationCount(): number {
    return this.generation;
  }

  getStats(): Readonly<typeof this.stats> {
    return { ...this.stats };
  }

  /**
   * Returns a snapshot of cached entries for safe use outside the lock.
   * The array is a shallow copy; entries themselves are shared (for _rev preservation).
   */
  getCachedEntriesSnapshot(): Log_Entry[] {
    return [...this._entries];
  }

  // --- Private Helpers ---

  // Helper method to convert DateTime to a day key for the cache
  private getDayKey(date: DateTime): string {
    return date.toISODate();
  }

  // Helper method to check if all days in a range are cached
  private areDaysInRange(startDate: DateTime, endDate: DateTime): boolean {
    let current = startDate.startOf('day');
    const end = endDate.startOf('day');

    while (current <= end) {
      if (!this._cachedDays.has(this.getDayKey(current))) {
        return false;
      }
      current = current.plus({ days: 1 });
    }
    return true;
  }

  // Helper method to add all days in a range to the cache
  private addDaysToCache(startDate: DateTime, endDate: DateTime): void {
    let current = startDate.startOf('day');
    const end = endDate.startOf('day');

    while (current <= end) {
      this._cachedDays.add(this.getDayKey(current));
      current = current.plus({ days: 1 });
    }
  }

  // check if a date is within the cached range
  private isTimeInCachedRange(timestamp_local: DateTime) {
    // check if the specific day is cached
    const dayKey = this.getDayKey(timestamp_local);
    return this._cachedDays.has(dayKey);
  }

  // Get one entry by id from the cache.
  // returns undefined if the entry doesn't exist, or 'needCaching' if the cached range needs to move to include this entry
  private findCachedEntryById(eid: string): Log_Entry | undefined | 'needCaching' {
    if (!this.isTimeInCachedRange(getEntryTimeFromId(eid).toLocal())) {
      this.stats.misses++;
      return 'needCaching';
    }
    const entry = this._entries.find(e => e._id === eid);
    if (entry) {
      this.stats.hits++;
    }
    return entry;
  }

  // Get all entries in between two timestamps
  // returns 'needCaching' if the cached range needs to move to include (a part of) this range
  private getCachedEntriesInRange(startTime_local: DateTime, endTime_local: DateTime): Log_Entry[] | 'needCaching' {
    // check if all days in the range are cached
    if (!this.areDaysInRange(startTime_local, endTime_local)) {
      this.stats.misses++;
      return 'needCaching';
    }
    this.stats.hits++;
    return this._entries.filter(e => {
      const time = getEntryTimeFromId(e._id).toLocal();
      return time >= startTime_local && time <= endTime_local;
    });
  }

  // Update the cache to include _at least_ the given range of days
  private async updateCacheAsync(startTime_local: DateTime, endTime_local: DateTime) {
    // always add at least one week before and after the requested range
    const firstDayBoD_local = startTime_local.minus({ days: 7 }).startOf('day');
    const lastDayEoD_local = endTime_local.plus({ days: 7 }).endOf('day');

    // Capture generation and cached entries while holding the lock
    // This ensures we detect if cache is cleared during the async operation
    const generationBeforeOp = this.generation;
    const cachedEntriesSnapshot = [...this._entries]; // Shallow copy of array, entries themselves are shared
    this.activeOperations++;

    let entries: Log_Entry[];
    try {
      // get the backlog in that range from either the database or generate it
      // Pass the cached entries snapshot to avoid direct access to this._entries during async operation
      entries = await this.deps.generateBacklogInMemoryAsync(firstDayBoD_local, lastDayEoD_local, cachedEntriesSnapshot);
    } finally {
      this.activeOperations--;
    }

    // if cache was cleared during operation, discard result and let caller retry
    if (this.generation !== generationBeforeOp) {
      this.stats.staleDiscards++;
      return;
    }

    // if generateBacklog returned empty for a range that the cache already covers elsewhere,
    // log for diagnostics but proceed — mergeLogEntries preserves existing entries
    if (entries.length === 0 && cachedEntriesSnapshot.length > 0) {
      console.log(`BacklogCache: generateBacklog returned empty for ${firstDayBoD_local.toISODate()}..${lastDayEoD_local.toISODate()} while cache had ${cachedEntriesSnapshot.length} entries.`);
    }

    // Use 'entries1' for revSource because entries already has correct _rev from disk merge inside generateBacklogInMemoryAsync
    // logging is passed even though 'entries1' never reaches the report (#4835): that
    // direction only fills in a MISSING revision and so cannot launder. Handing it over
    // anyway means a later change of direction keeps the report instead of losing it
    // silently.
    this._entries = mergeLogEntries(entries, this._entries, this.deps.getDatabase(), 'entries1', this.deps.logging);

    // add the loaded days to our cache tracking
    this.addDaysToCache(firstDayBoD_local, lastDayEoD_local);
  }

  // --- Public Async Methods (Lock-Protected) ---

  async getEntryByIdAsync(eid: string): Promise<Log_Entry> {
    const result = await this.cachingLock.exclusiveCall(async () => {
      let entry = this.findCachedEntryById(eid);
      if (entry === 'needCaching') {
        const time = getEntryTimeFromId(eid).toLocal();
        await this.updateCacheAsync(time, time);
        entry = this.findCachedEntryById(eid);
        if (entry === 'needCaching') {
          console.log(`BacklogCache: Failed to cache entry ${eid} (stale discard or empty range)`);
          return undefined;
        }
      }
      return entry;
    });
    return result;
  }

  async getEntriesInRangeAsync(startTime_local: DateTime, endTime_local: DateTime, options?: { inLogEntriesTimeZone?: boolean }): Promise<Log_Entry[]> {
    const result = await this.cachingLock.exclusiveCall(async () => {
      const inLogEntriesTimeZone = options && options.inLogEntriesTimeZone || false;
      let entries = this.getCachedEntriesInRange(startTime_local, endTime_local);
      if (entries === 'needCaching') {
        await this.updateCacheAsync(startTime_local, endTime_local);
        entries = this.getCachedEntriesInRange(startTime_local, endTime_local);
        if (entries === 'needCaching') {
          console.log(`BacklogCache: Failed to cache entries for ${startTime_local.toISO()}  .. ${endTime_local.toISO()} (stale discard or empty range)`);
          return [];
        }
      }
      if (inLogEntriesTimeZone) {
        entries = entries.filter(e => getEntryLocalTimeInDesiredTimeZone(e, startTime_local) >= startTime_local && getEntryLocalTimeInDesiredTimeZone(e, endTime_local) <= endTime_local);
      }
      return entries;
    });
    return result;
  };

  async clear() {
    await this.cachingLock.exclusiveCall(async () => {
      // #5640: emptying this cache makes the previous-workout look-back unanswerable for as
      // long as it stays empty, and nothing else records that it happened. The breadcrumb is
      // what lets a later Sentry report name the screen that emptied it.
      console.log('[BacklogCache] clear', { entriesDropped: this._entries.length });
      this._entries = [];
      this._cachedDays.clear();
      this.generation++;
      this.stats.clears++;
    });
  }

  // clear cache for specific days only, used to avoid full cache thrashing
  // when background sync pulls old log entries outside the current view
  async clearDays(days: Set<string>) {
    await this.cachingLock.exclusiveCall(async () => {
      let hasOverlap = false;
      for (const day of days) {
        if (this._cachedDays.has(day)) {
          hasOverlap = true;
          break;
        }
      }
      if (!hasOverlap) return;

      for (const day of days) {
        this._cachedDays.delete(day);
      }
      // extract UTC date directly from doc ID (log/{YYYY-MM-DD}T...) to match how callers build day keys
      this._entries = this._entries.filter(e => {
        const dayKey = e._id.substring(4, 14);
        return !days.has(dayKey);
      });
      this.generation++;
      this.stats.clears++;
    });
  }

  // --- Synchronous Read Methods ---

  /**
   * Find the previous workout in the cache (any state).
   * Returns null if no previous workout exists.
   *
   * NOTE: This method operates on the current cache state without acquiring the lock,
   * suitable for synchronous UI reads where eventual consistency is acceptable.
   */
  findPreviousWorkoutInCache(logWorkout: Log_Workout): Log_Workout | null {
    return this.findPreviousWorkoutInCacheWithCoverage(logWorkout).previous;
  }

  /**
   * The same look-back, plus how far back this cache actually searched (#5640).
   *
   * A `previous` of null on its own says nothing: an emptied cache, and a cache whose days
   * never reached back to the user's first workout, both look exactly like a user who has
   * never trained. `searchedFromDay` is what lets the caller tell those apart — compare it
   * with the day the user's log starts. Without that comparison the answer is a guess, and
   * writing a guess into a workout document is the bug this exists to prevent.
   *
   * It is the earliest day reachable on an UNBROKEN run back from the workout, not the
   * earliest day held overall — see `earliestUnbrokenCachedDay`, which says why the
   * difference matters. It is undefined whenever a previous workout WAS found, because
   * nobody asks how far back the search went in that case.
   */
  findPreviousWorkoutInCacheWithCoverage(
    logWorkout: Log_Workout,
  ): { previous: Log_Workout | null; searchedFromDay: string | undefined } {
    const previous = this.walkBackToPreviousWorkout(logWorkout);
    // only worked out when the walk found nothing — that is the only case anybody asks about,
    // and every synchronous display read comes through here on the render path
    return { previous, searchedFromDay: previous ? undefined : this.earliestUnbrokenCachedDay(logWorkout) };
  }

  /**
   * The earliest day this cache can vouch for on an UNBROKEN run back from the given entry's
   * own day — or undefined when it does not even hold that day (#5640).
   *
   * Contiguity is the whole point. `_cachedDays` is a set with no promise of being a run:
   * `clearDays` removes arbitrary days along with their entries, and two separate range reads
   * leave two blocks with a gap between them. A previous workout sitting in such a gap is
   * invisible to the walk above, so the earliest day held overall would vouch for days the
   * cache never actually searched.
   */
  private earliestUnbrokenCachedDay(logWorkout: Log_Workout): string | undefined {
    let day = getEntryTimeFromId(logWorkout._id).toLocal().startOf('day');
    if (!this._cachedDays.has(this.getDayKey(day))) {
      return undefined;
    }
    // step back one day at a time for as long as the cache holds each one
    while (true) {
      const previousDay = day.minus({ days: 1 });
      const previousKey = this.getDayKey(previousDay);
      // Stop unless the step actually went backwards. Subtracting a day does not always reach
      // an earlier date: a zone that skipped a calendar day outright (Pacific/Apia has no
      // 2011-12-30) has no such local midnight, and Luxon moves a non-existent local time
      // forward — handing back the day we started on. Without this the loop would never end,
      // and it runs on the render path, so it would hang the screen rather than answer wrongly.
      if (previousKey >= this.getDayKey(day) || !this._cachedDays.has(previousKey)) {
        return this.getDayKey(day);
      }
      day = previousDay;
    }
  }

  /**
   * Walk backwards through the cached entries from where this workout sits, and hand back the
   * first workout found before it — or null when the cached entries hold none. Null on its own
   * says nothing about whether one EXISTS; that is what the coverage answer above is for.
   */
  private walkBackToPreviousWorkout(logWorkout: Log_Workout): Log_Workout | null {
    const allEntries = this._entries;
    let currentIndex = allEntries.findIndex(e => e._id === logWorkout._id);

    // if not in cache, find where it would fit based on its date
    if (currentIndex < 0) {
      const workoutDate = logWorkout.time_utc;
      currentIndex = allEntries.findIndex(e => e.time_utc > workoutDate);
      if (currentIndex < 0) {
        currentIndex = allEntries.length;
      }
    }

    // look backwards for the first workout
    for (let i = currentIndex - 1; i >= 0; i--) {
      const entry = allEntries[i];
      if (entry.type === LogRecordType.Workout) {
        return entry as Log_Workout;
      }
    }

    return null;
  }
}
